import { ApiCoreClient } from '@api/ApiCoreClient';
import {
  IncomingActorUpdatedMessage,
  IncomingAvatarNameUpdatedMessage,
  IncomingDisplayNameUpdatedMessage,
  IncomingParticipantJoinedMessage,
  IncomingParticipantLeftMessage,
  IncomingParticipantUpdatedMessage,
  IncomingSetObserverResponseMessage,
} from '@api/roomState/types/socketProtocol';
import { ApiSubClient } from './ApiSubClient';

export class ParticipantClient extends ApiSubClient {
  constructor(core: ApiCoreClient) {
    super(core);
    core.socket.on('message:participantJoined', this.onParticipantJoined);
    core.socket.on('message:participantLeft', this.onParticipantLeft);
    core.socket.on('message:participantUpdated', this.onParticipantUpdated);
    core.socket.on('message:actorUpdated', this.onActorUpdated);
    core.socket.on('message:displayNameUpdated', this.onDisplayNameUpdated);
    core.socket.on('message:avatarNameUpdated', this.onAvatarNameUpdated);
  }

  private onParticipantJoined = (message: IncomingParticipantJoinedMessage) => {
    this.core.cacheApi.addSession(message);
  };

  private onParticipantLeft = (message: IncomingParticipantLeftMessage) => {
    this.core.cacheApi.deleteSession(message.payload);
  };

  private onParticipantUpdated = (message: IncomingParticipantUpdatedMessage) => {
    this.core.cacheApi.updateUser({
      id: message.sender.actorId,
      participantState: message.payload.participantState,
      isObserver: message.payload.isObserver,
    });
  };

  private onActorUpdated = (message: IncomingActorUpdatedMessage) => {
    this.core.cacheApi.updateUser({
      id: message.sender.actorId,
      actor: message.payload.actor,
    });

    if (this.core.actor?.actorId === message.sender.actorId) {
      const actorUpdates = Object.fromEntries(
        Object.entries(message.payload.actor).filter(([, value]) => value !== undefined)
      );
      this.core.patchActor(actorUpdates);
    }
  };

  private onDisplayNameUpdated = (message: IncomingDisplayNameUpdatedMessage) => {
    this.core.cacheApi.updateUser({
      id: message.sender.actorId,
      actor: {
        displayName: message.payload.displayName,
      },
    });

    if (this.core.actor?.actorId === message.sender.actorId) {
      this.core.patchActor({ displayName: message.payload.displayName });
    }
  };

  private onAvatarNameUpdated = (message: IncomingAvatarNameUpdatedMessage) => {
    this.core.cacheApi.updateUser({
      id: message.sender.actorId,
      actor: {
        avatarName: message.payload.avatarName,
      },
    });

    if (this.core.actor?.actorId === message.sender.actorId) {
      this.core.patchActor({ avatarName: message.payload.avatarName });
    }
  };

  // Public API

  enterMeeting = async () => {
    if (!this.core.actor || !this.core.roomId) {
      return;
    }

    this.core.cacheApi.updateUser({
      id: this.core.actor.actorId,
      isObserver: false,
    });

    try {
      await this.core.socket.sendAndWaitForResponse<IncomingSetObserverResponseMessage>(
        {
          kind: 'setObserver',
          payload: { isObserver: false },
        },
        5_000
      );
    } catch {
      return;
    }
  };

  updateDisplayName = (displayName: string) => {
    this.updateIdentity({ displayName });
  };

  updateAvatarName = (avatarName: string) => {
    this.updateIdentity({ avatarName });
  };

  updateIdentity = (identity: { displayName?: string; avatarName?: string }) => {
    if (!this.core.actor) {
      throw new Error('Must be authenticated to update identity');
    }

    const actorUpdates = Object.fromEntries(
      Object.entries(identity).filter(([, value]) => typeof value === 'string')
    );
    if (Object.keys(actorUpdates).length === 0) {
      return;
    }

    this.core.cacheApi.updateUser({ actor: actorUpdates, id: this.core.actor.actorId });
    this.core.patchActor(actorUpdates);
    this.core.socket.send({
      kind: 'updateSelfIdentity',
      payload: actorUpdates,
    });
  };
}
